import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import { cloneDeep } from 'lodash';
import { InputComponent, SortDirections, TableCellDirective, TableGridComponent, TableHeader } from 'mbs-ui-kit';
import {
  AdministratorInCamelCase,
  AdministratorsQueryParams,
  AdministratorsSortBy,
  tableSelectAllEntitiesText,
  tableSelectedEntitiesText
} from 'mbs-ui-shared';
import { AdministratorsFacade } from 'mbs-ui-shared@facades/administrators.facade';
import { splitArrayIntoChunks } from 'mbs-ui-shared@modules/utility-selection-modal/const';
import { BehaviorSubject, Observable, debounceTime, filter, forkJoin, map, of, switchMap, tap } from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'mbs-select-admin-utility',
  templateUrl: './select-admin-utility.component.html',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TableCellDirective,
    TableGridComponent,
    I18NextPipe,
    TableGridComponent,
    InputComponent
  ]
})
export class SelectAdminUtilityComponent implements OnInit, OnChanges {
  private adminFacade = inject(AdministratorsFacade);
  private i18next = inject(I18NextPipe);

  @Input() selectedIds: string[] = [];
  @Input() multipleSelect = false;
  @Input() noDataText: string = null;
  @Output() emitChanges = new EventEmitter<AdministratorInCamelCase[]>();

  public loading = true;

  public headers: TableHeader[] = [
    {
      name: 'Email',
      overflow: true,
      class: 'pl-0',
      gridColSize: '10fr'
    }
  ];

  public adminSearchForm = new FormGroup<{ name: FormControl<string> }>({
    name: new FormControl(null)
  });

  public infiniteScrollDistance = 0.5;
  public infiniteScrollThrottle = 150;
  public infiniteScrollLoading = false;
  public orderBy: `${AdministratorsSortBy}:${SortDirections}` = `${AdministratorsSortBy.Email}:${SortDirections.Ascending}`;
  public total = 0;

  public data: AdministratorInCamelCase[] = [];
  public selected: AdministratorInCamelCase[] = [];

  public onScrolled$ = new BehaviorSubject<boolean>(false);

  private currentParams: AdministratorsQueryParams = null;
  private limit = 100;
  private offset = 0;

  constructor() {
    this.loading = true;

    this.getLoadStreamWithParams().subscribe({
      next: () => (this.loading = false),
      error: () => {
        this.loading = false;
        this.data = [];
      }
    });
  }

  updateStore(admins: AdministratorInCamelCase[]) {
    this.adminFacade.setEntities(cloneDeep(admins));
  }

  loadSelected() {
    if (!this.selectedIds.length) return;

    this.loading = true;

    const getLimitedListStream$ = splitArrayIntoChunks(this.selectedIds).map((idList) =>
      this.adminFacade.getAdministrators({ ids: idList }).pipe(map((response) => response.data))
    );

    forkJoin(getLimitedListStream$)
      .pipe(
        tap(() => (this.loading = true)),
        map((response) => response.reduce((ac, next) => (ac ?? []).concat(next), null)),
        untilDestroyed(this)
      )
      .subscribe({
        next: (data: AdministratorInCamelCase[]) => {
          this.selected = data;
          this.data = this.getNewDataAndKeepSelected(this.data);
          this.selectedIds = [];
          this.updateStore(this.selected);
          this.loading = false;
        },
        error: () => {
          this.selected = [];
          this.loading = false;
        }
      });
  }

  getNewDataAndKeepSelected(data: AdministratorInCamelCase[]) {
    const selectedToBeAdded = this.selected.filter((item) => data.every((dataItem) => dataItem.id !== item.id));
    return selectedToBeAdded.concat(data);
  }

  getLoadStreamWithParams(
    params: AdministratorsQueryParams = { offset: this.offset, limit: this.limit, orderBy: this.orderBy },
    isInfiniteScroll = false
  ) {
    return this.adminFacade.getAdministrators(params).pipe(
      tap((response) => {
        this.currentParams = { ...params };
        this.total = response.total;
        const previousLength = this.data.length;

        if (params.offset > 0) {
          this.data = this.data.concat(response.data.filter((item) => this.data.every((dataItem) => dataItem.id !== item.id)));
        } else {
          this.data = this.getNewDataAndKeepSelected(response.data);
        }

        if (isInfiniteScroll && previousLength === this.data.length) this.onScrolled$.next(true);

        this.updateStore(this.data);
      })
    );
  }

  ngOnInit(): void {
    this.onScrolled$
      .pipe(
        filter((value) => value && this.data.length < this.total),
        tap(() => (this.infiniteScrollLoading = true)),
        switchMap(() =>
          this.getLoadStreamWithParams(
            { ...this.currentParams, orderBy: this.orderBy, offset: this.currentParams.offset + this.limit },
            true
          )
        ),
        untilDestroyed(this)
      )
      .subscribe({
        next: () => (this.infiniteScrollLoading = false),
        error: () => {
          this.infiniteScrollLoading = false;
          this.data = [];
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedIds) {
      this.loadSelected();
    }
  }

  clear() {
    this.search();
  }

  search() {
    of(this.adminSearchForm.get('name').value)
      .pipe(
        switchMap((name) => {
          if (name) {
            this.currentParams.search = name;
          } else {
            delete this.currentParams.search;
          }

          this.loading = true;

          return this.getLoadStreamWithParams({ ...this.currentParams, offset: 0, limit: this.limit, orderBy: this.orderBy });
        }),
        untilDestroyed(this)
      )
      .subscribe({
        next: () => (this.loading = false),
        error: () => {
          this.loading = false;
          this.data = [];
        }
      });
  }

  onSelectedRowsChanged(items: AdministratorInCamelCase[]) {
    this.selected = items;
    this.emitChanges.emit(this.selected);
  }

  public trackBy(item) {
    return item.id;
  }

  public selectedText(): string {
    return tableSelectedEntitiesText({
      i18nPipe: this.i18next,
      count: this.selected.length,
      isSelectAllOnAllPages: false,
      entityPluralKey: 'shared:common.user'
    });
  }

  public selectAllText(): string {
    return tableSelectAllEntitiesText({
      i18nPipe: this.i18next,
      total: this.total,
      entityPluralKey: 'shared:common.user'
    });
  }

  public selectAllOnAllPages(should: boolean) {
    if (should && this.total !== this.data.length) {
      const streams$: Observable<any>[] = [];
      const amountOfLoads = Math.round((this.total - this.data.length) / this.limit);

      for (let i = 1; i <= amountOfLoads; i++)
        streams$.push(
          this.getLoadStreamWithParams({
            ...this.currentParams,
            orderBy: this.orderBy,
            offset: this.currentParams.offset + i * this.limit
          })
        );

      this.loading = true;

      forkJoin(streams$)
        .pipe(debounceTime(300), untilDestroyed(this))
        .subscribe({
          next: () => {
            this.total = this.data.length;
            this.selected = cloneDeep(this.data);
            this.loading = false;
            this.emitChanges.emit(this.selected);
          },
          error: () => {
            this.loading = false;
          }
        });
    }
  }
}
